<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useCartStore } from "/src/stores/useCartStore.js";
import { useMeta } from "quasar";

useMeta(() => {
  return {
    title: "Privacy Policy — SML Global & Oraklenz",
    meta: {
      description: {
        name: "description",
        content:
          "Privacy Policy and legal agreement governing the use of SML Global and Oraklenz websites and services.",
      },
    },
  };
});

const cartStore = useCartStore();
const isCartOpen = ref(false);
const isCheckoutStep = ref(false);
const checkoutForm = ref({
  name: "",
  email: "",
  address: "",
  card: "",
  exp: "",
  cvv: "",
});

const activeSection = ref("introduction");

const sections = [
  { id: "introduction", label: "Introduction" },
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "why-we-collect", label: "Why We Collect Information" },
  { id: "use-of-information", label: "Use of Information Collected" },
  { id: "disclosure", label: "Disclosure of Information" },
  { id: "non-marketing", label: "Non-Marketing Purposes" },
  { id: "children", label: "Children under the age of 13" },
  { id: "unsubscribe", label: "Unsubscribe or Opt-Out" },
  { id: "links", label: "Links to Other Websites" },
  { id: "eu-users", label: "Notice to European Union Users" },
  { id: "data-subject-rights", label: "Your Rights as a Data Subject" },
  { id: "security", label: "Security" },
  { id: "cookies", label: "Cookies" },
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "contact", label: "How to Contact Us" },
];

const scrollToSection = (id) => {
  activeSection.value = id;
  const element = document.getElementById(id);
  if (element) {
    const yOffset = -90;
    const y =
      element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const handleScroll = () => {
  const scrollPosition = window.scrollY + 120;
  for (let i = sections.length - 1; i >= 0; i--) {
    const el = document.getElementById(sections[i].id);
    if (el && el.offsetTop <= scrollPosition) {
      activeSection.value = sections[i].id;
      break;
    }
  }
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});

const removeFromCart = (key) => {
  if (typeof cartStore.remove === "function") {
    cartStore.remove(key);
  }
};

const checkout = () => {
  if (cartStore.items.length === 0) {
    alert("Your cart is empty.");
    return;
  }
  isCheckoutStep.value = true;
};

const completeOrder = () => {
  alert("Order placed successfully! This is a placeholder.");
  if (typeof cartStore.items !== "undefined") {
    cartStore.items = [];
  }
  isCheckoutStep.value = false;
  isCartOpen.value = false;
};

const handleDialogHide = () => {
  isCheckoutStep.value = false;
};
</script>

<template>
  <q-page class="legal-page">
    <!-- Announcement Bar -->
    <div id="announce_id" class="announce">
      <b>Same active ingredient as leading perio gels.</b> ~20% less per tube.
      No subscription required.
    </div>

    <!-- Hero / Title Section -->
    <section class="legal-hero">
      <div class="wrap">
        <div class="breadcrumbs">
          <router-link to="/"> Home </router-link>
          <span class="separator">/</span>
          <span class="current">Privacy Policy</span>
        </div>

        <div class="hero-content">
          <span class="eyebrow">Legal & Compliance</span>
          <h1 class="page-title">Privacy Policy</h1>
          <p class="hero-subtitle">ONLINE PRIVACY POLICY AGREEMENT</p>

          <div class="meta-badge-row">
            <div class="meta-badge">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>Effective Date: <strong>01/01/2020</strong></span>
            </div>
            <div class="meta-badge">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>SML - Space Maintainer Laboratories</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Notice Banner -->
    <div class="wrap q-my-lg">
      <div class="notice-callout">
        <div class="callout-icon">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div class="callout-text">
          <strong>PLEASE READ CAREFULLY:</strong> SML - Space Maintainer
          Laboratories (SML) values its users' privacy. This Privacy Policy
          ("Policy") will help you understand how we collect and use personal
          information from those who visit our website or make use of our online
          facilities and services.
        </div>
      </div>
    </div>

    <!-- Main Content Layout with Sticky Sidebar -->
    <div class="wrap legal-body-layout">
      <!-- Sticky Navigation Sidebar -->
      <aside class="legal-sidebar">
        <div class="sidebar-inner">
          <div class="sidebar-header">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            <span>Table of Contents</span>
          </div>

          <nav class="toc-list">
            <button
              v-for="(item, index) in sections"
              :key="item.id"
              class="toc-link"
              :class="{ active: activeSection === item.id }"
              @click="scrollToSection(item.id)"
            >
              <span class="toc-num">{{ index + 1 }}.</span>
              <span class="toc-label">{{ item.label }}</span>
            </button>
          </nav>
        </div>
      </aside>

      <!-- Legal Content Articles -->
      <main class="legal-content">
        <!-- Introduction -->
        <section id="introduction" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 1</div>
            <h2>Introduction</h2>
          </div>
          <div class="card-content">
            <p>
              SML - Space Maintainer Laboratories (SML) values its users'
              privacy. This Privacy Policy ("Policy") will help you understand
              how we collect and use personal information from those who visit
              our website or make use of our online facilities and services, and
              what we will and will not do with the information we collect. Our
              Policy has been designed and created to ensure those affiliated
              with SML - Space Maintainer Laboratories of our commitment and
              realization of our obligation not only to meet, but to exceed,
              most existing privacy standards.
            </p>
            <p>
              We reserve the right to make changes to this Policy at any given
              time. If you want to make sure that you are up to date with the
              latest changes, we advise you to frequently visit this page. If at
              any point in time SML - Space Maintainer Laboratories decides to
              make use of any personally identifiable information on file, in a
              manner vastly different from that which was stated when this
              information was initially collected, the user or users shall be
              promptly notified by email. Users at that time shall have the
              option as to whether to permit the use of their information in
              this separate manner.
            </p>
            <p>
              This Policy applies to SML - Space Maintainer Laboratories , and
              it governs any and all data collection and usage by us. Through
              the use of ww.SMLglobal.com, you are therefore consenting to the
              data collection procedures expressed in this Policy.
            </p>
            <p>
              Please note that this Policy does not govern the collection and
              use of information by companies that SML - Space Maintainer
              Laboratories does not control, nor by individuals not employed or
              managed by us. If you visit a website that we mention or link to,
              be sure to review its privacy policy before providing the site
              with information. It is highly recommended and suggested that you
              review the privacy policies and statements of any website you
              choose to use or frequent to better understand the way in which
              websites garner, make use of and share the information collected.
            </p>
            <p>Specifically, this Policy will inform you of the following:</p>
            <ol class="styled-numbered-list">
              <li>
                What personally identifiable information is collected from you
                through our website;
              </li>
              <li>
                Why we collect personally identifiable information and the legal
                basis for such collection;
              </li>
              <li>
                How we use the collected information and with whom it may be
                shared;
              </li>
              <li>
                What choices are available to you regarding the use of your
                data; and
              </li>
              <li>
                The security procedures in place to protect the misuse of your
                information.
              </li>
            </ol>
          </div>
        </section>

        <!-- Information We Collect -->
        <section id="information-we-collect" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 2</div>
            <h2>Information We Collect</h2>
          </div>
          <div class="card-content">
            <p>
              It is always up to you whether to disclose personally identifiable
              information to us, although if you elect not to do so, we reserve
              the right not to register you as a user or provide you with any
              products or services. This website collects various types of
              information, such as:
            </p>
            <p>
              Voluntarily provided information which may include your name,
              address, email address, billing and/or credit card information
              etc. which may be used when you purchase products and/or services
              and to deliver the services you have requested.
            </p>
            <p>
              In addition, SML - Space Maintainer Laboratories may have the
              occasion to collect non-personal anonymous demographic
              information, such as age, gender, household income, political
              affiliation, race and religion, as well as the type of browser you
              are using, IP address, or type of operating system, which will
              assist us in providing and maintaining superior quality service.
            </p>
            <p>
              Please rest assured that this site will only collect personal
              information that you knowingly and willingly provide to us by way
              of surveys, completed membership forms, and emails. It is the
              intent of this site to use personal information only for the
              purpose for which it was requested, and any additional uses
              specifically provided for on this Policy.
            </p>
          </div>
        </section>

        <!-- Why We Collect Information -->
        <section id="why-we-collect" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 3</div>
            <h2>Why We Collect Information and For How Long</h2>
          </div>
          <div class="card-content">
            <p>We are collecting your data for several reasons:</p>
            <ul class="styled-list">
              <li>
                To better understand your needs and provide you with the
                services you have requested;
              </li>
              <li>
                To fulfill our legitimate interest in improving our services and
                products;
              </li>
              <li>
                To send you promotional emails containing information we think
                you may like when we have your consent to do so;
              </li>
              <li>
                To contact you to fill out surveys or participate in other types
                of market research, when we have your consent to do so;
              </li>
              <li>
                To customize our website according to your online behavior and
                personal preferences.
              </li>
            </ul>
            <p>
              The data we collect from you will be stored for no longer than
              necessary. The length of time we retain said information will be
              determined based upon the following criteria: the length of time
              your personal information remains relevant; the length of time it
              is reasonable to keep records to demonstrate that we have
              fulfilled our duties and obligations; any limitation periods
              within which claims might be made; any retention periods
              prescribed by law or recommended by regulators, professional
              bodies or associations; the type of contract we have with you, the
              existence of your consent, and our legitimate interest in keeping
              such information as stated in this Policy.
            </p>
          </div>
        </section>

        <!-- Use of Information Collected -->
        <section id="use-of-information" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 4</div>
            <h2>Use of Information Collected</h2>
          </div>
          <div class="card-content">
            <p>
              SML - Space Maintainer Laboratories does not now, nor will it in
              the future, sell, rent or lease any of its customer lists and/or
              names to any third parties.
            </p>
            <p>
              SML - Space Maintainer Laboratories may collect and may make use
              of personal information to assist in the operation of our website
              and to ensure delivery of the services you need and request. At
              times, we may find it necessary to use personally identifiable
              information as a means to keep you informed of other possible
              products and/or services that may be available to you from
              ww.SMLglobal.com
            </p>
            <p>
              SML - Space Maintainer Laboratories may also be in contact with
              you with regards to completing surveys and/or research
              questionnaires related to your opinion of current or potential
              future services that may be offered.
            </p>
            <p>
              SML - Space Maintainer Laboratories uses various third-party
              social media features including but not limited to Facebook,
              Twitter, Linkedin, Blog, Venmo, Pinterest, Snap Chat, Youtube and
              other interactive programs. These may collect your IP address and
              require cookies to work properly. These services are governed by
              the privacy policies of the providers and are not within SML -
              Space Maintainer Laboratories's control.
            </p>
          </div>
        </section>

        <!-- Disclosure of Information -->
        <section id="disclosure" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 5</div>
            <h2>Disclosure of Information</h2>
          </div>
          <div class="card-content">
            <p>
              SML - Space Maintainer Laboratories may not use or disclose the
              information provided by you except under the following
              circumstances:
            </p>
            <ul class="styled-list">
              <li>
                as necessary to provide services or products you have ordered;
              </li>
              <li>
                in other ways described in this Policy or to which you have
                otherwise consented;
              </li>
              <li>
                in the aggregate with other information in such a way so that
                your identity cannot reasonably be determined;
              </li>
              <li>
                as required by law, or in response to a subpoena or search
                warrant;
              </li>
              <li>
                to outside auditors who have agreed to keep the information
                confidential;
              </li>
              <li>as necessary to enforce the Terms of Service;</li>
              <li>
                as necessary to maintain, safeguard and preserve all the rights
                and property of SML Space Maintainer Laboratories .
              </li>
            </ul>
          </div>
        </section>

        <!-- Non-Marketing Purposes -->
        <section id="non-marketing" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 6</div>
            <h2>Non-Marketing Purposes</h2>
          </div>
          <div class="card-content">
            <p>
              SML - Space Maintainer Laboratories greatly respects your privacy.
              We do maintain and reserve the right to contact you if needed for
              non-marketing purposes (such as bug alerts, security breaches,
              account issues, and/or changes in SML - Space Maintainer
              Laboratories products and services). In certain circumstances, we
              may use our website, newspapers, or other public means to post a
              notice.
            </p>
          </div>
        </section>

        <!-- Children under the age of 13 -->
        <section id="children" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 7</div>
            <h2>Children under the age of 13</h2>
          </div>
          <div class="card-content">
            <p>
              SML - Space Maintainer Laboratories's website is not directed to,
              and does not knowingly collect personal identifiable information
              from, children under the age of thirteen (13). If it is determined
              that such information has been inadvertently collected on anyone
              under the age of thirteen (13), we shall immediately take the
              necessary steps to ensure that such information is deleted from
              our system's database, or in the alternative, that verifiable
              parental consent is obtained for the use and storage of such
              information. Anyone under the age of thirteen (13) must seek and
              obtain parent or guardian permission to use this website.
            </p>
          </div>
        </section>

        <!-- Unsubscribe or Opt-Out -->
        <section id="unsubscribe" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 8</div>
            <h2>Unsubscribe or Opt-Out</h2>
          </div>
          <div class="card-content">
            <p>
              All users and visitors to our website have the option to
              discontinue receiving communications from us by way of email or
              newsletters. To discontinue or unsubscribe from our website please
              send an email that you wish to unsubscribe to cs@smlglobal.com. If
              you wish to unsubscribe or opt-out from any third-party websites,
              you must go to that specific website to unsubscribe or optout. SML
              - Space Maintainer Laboratories will continue to adhere to this
              Policy with respect to any personal information previously
              collected.
            </p>
          </div>
        </section>

        <!-- Links to Other Websites -->
        <section id="links" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 9</div>
            <h2>Links to Other Websites</h2>
          </div>
          <div class="card-content">
            <p>
              Our website does contain links to affiliate and other websites.
              SML - Space Maintainer Laboratories does not claim nor accept
              responsibility for any privacy policies, practices and/or
              procedures of other such websites. Therefore, we encourage all
              users and visitors to be aware when they leave our website and to
              read the privacy statements of every website that collects
              personally identifiable information. This Privacy Policy Agreement
              applies only and solely to the information collected by our
              website.
            </p>
          </div>
        </section>

        <!-- Notice to European Union Users -->
        <section id="eu-users" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 10</div>
            <h2>Notice to European Union Users</h2>
          </div>
          <div class="card-content">
            <p>
              SML - Space Maintainer Laboratories's operations are located
              primarily in the United States. If you provide information to us,
              the information will be transferred out of the European Union (EU)
              and sent to the United States. (The adequacy decision on the EU-US
              Privacy became operational on August 1, 2016. This framework
              protects the fundamental rights of anyone in the EU whose personal
              data is transferred to the United States for commercial purposes.
              It allows the free transfer of data to companies that are
              certified in the US under the Privacy Shield.) By providing
              personal information to us, you are consenting to its storage and
              use as described in this Policy.
            </p>
          </div>
        </section>

        <!-- Your Rights as a Data Subject -->
        <section id="data-subject-rights" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 11</div>
            <h2>Your Rights as a Data Subject</h2>
          </div>
          <div class="card-content">
            <p>
              Under the regulations of the General Data Protection Regulation
              ("GDPR") of the EU you have certain rights as a Data Subject.
              These rights are as follows:
            </p>
            <ul class="styled-list">
              <li>
                <strong>The right to be informed:</strong> this means we must
                inform you of how we intend to use your personal data and we do
                this through the terms of this Policy.
              </li>
              <li>
                <strong>The right of access:</strong> this means you have the
                right to request access to the data we hold about you and we
                must respond to those requests within one month. You can do this
                by sending an email to Info@smlglobal.com.
              </li>
              <li>
                <strong>The right to rectification:</strong> this means that if
                you believe some of the data we hold is incorrect, you have the
                right to have it corrected. You can do this by logging into your
                account with us, or by sending us an email with your request.
              </li>
              <li>
                <strong>The right to erasure:</strong> this means you can
                request that the information we hold be deleted, and we will
                comply unless we have a compelling reason not to, in which case
                you will be informed of same. You can do this by sending an
                email to Info@smlglobal.com.
              </li>
              <li>
                <strong>The right to restrict processing:</strong> this means
                you can change your communication preferences or opt-out of
                certain communications. You can do this by sending an email to
                cs@smlglobal.com.
              </li>
              <li>
                <strong>The right of data portability:</strong> this means you
                can obtain and use the data we hold for your own purposes
                without explanation. If you wish to request a copy of your
                information, contact us at Info@smlglobal.com.
              </li>
              <li>
                <strong>The right to object:</strong> this means you can file a
                formal objection with us regarding our use of your information
                with regard to third parties, or its processing where our legal
                basis is our legitimate interest in it. To do this, please send
                an email to Info@smlglobal.com.
              </li>
            </ul>
            <p>
              In addition to the rights above, please rest assured that we will
              always aim to encrypt and anonymize your personal information
              whenever possible. We also have protocols in place in the unlikely
              event that we suffer a data breach and we will contact you if your
              personal information is ever at risk. For more details regarding
              our security protections see the section below or visit our
              website at ww.SMLglobal.com.
            </p>
          </div>
        </section>

        <!-- Security -->
        <section id="security" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 12</div>
            <h2>Security</h2>
          </div>
          <div class="card-content">
            <p>
              SML - Space Maintainer Laboratories takes precautions to protect
              your information. When you submit sensitive information via the
              website, your information is protected both online and offline.
              Wherever we collect sensitive information (e.g. credit card
              information), that information is encrypted and transmitted to us
              in a secure way. You can verify this by looking for a lock icon in
              the address bar and looking for "https" at the beginning of the
              address of the webpage.
            </p>
            <p>
              While we use encryption to protect sensitive information
              transmitted online, we also protect your information offline. Only
              employees who need the information to perform a specific job (for
              example, billing or customer service) are granted access to
              personally identifiable information. The computers and servers in
              which we store personally identifiable information are kept in a
              secure environment. This is all done to prevent any loss, misuse,
              unauthorized access, disclosure or modification of the user's
              personal information under our control.
            </p>
            <p>
              The company also uses Secure Socket Layer (SSL) for authentication
              and private communications to build users' trust and confidence in
              the internet and website use by providing simple and secure access
              and communication of credit card and personal information.
            </p>
          </div>
        </section>

        <!-- Cookies -->
        <section id="cookies" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 13</div>
            <h2>Cookies</h2>
          </div>
          <div class="card-content">
            <p>
              Our website ("the Site") uses cookies and tracking technologies to
              distinguish you from other users of the Site. This helps us to
              provide you with a good experience when you browse the Site and
              also allows us to improve our site. By continuing to browse the
              Site, you are agreeing to our use of cookies.
            </p>
            <p>
              A cookie is a small file of letters and numbers that we store on
              your browser or the hard drive of your computer if you agree.
              Cookies contain information that is transferred to your computer's
              hard drive.
            </p>
            <p>We use the following cookies:</p>
            <ul class="styled-list">
              <li>
                <strong>Strictly necessary cookies.</strong> These are cookies
                that are required for the operation of the Site. They include,
                for example, cookies that enable you to log into secure areas of
                the Site.
              </li>
              <li>
                <strong>Analytical/performance cookies.</strong> They allow us
                to recognize and count the number of visitors, to see where our
                visitors are located and how visitors move around the site when
                they are using it. This helps us to improve the way the Site
                works, for example, by ensuring that users are finding what they
                are looking for easily. We use Google Analytics for these
                purposes. The service Google Analytics provides involves the use
                of cookies that collect information such as your IP address or
                other identifiers, browser information and information about the
                content you view and interact with to record how you use the
                Site.
              </li>
              <li>
                <strong>Functionality cookies.</strong> These are used to
                recognize you when you return to the Site. This enables us to
                personalize content for you and remember your preferences.
              </li>
            </ul>
            <p>We do not use the following cookies:</p>
            <ul class="styled-list">
              <li>
                <strong>Targeting cookies.</strong> These cookies record your
                visit to the Site, the pages you have visited and the links you
                have followed. We will use this information to make the Site
                more relevant to your interests. We may also share this
                information with third parties for this purpose.
              </li>
            </ul>
            <p>
              Please note that third parties (including, for example,
              advertising networks and providers of external services like web
              traffic analysis services) may also use cookies, over which we
              have no control. These cookies are likely to be
              analytical/performance cookies or targeting cookies.
            </p>
            <p>
              You block cookies by activating the setting on your browser that
              allows you to refuse the setting of all or some cookies. If you
              use your browser settings to block all cookies (including
              essential cookies) however, you may not be able to access all or
              parts of the Site.
            </p>
          </div>
        </section>

        <!-- Acceptance of Terms -->
        <section id="acceptance" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 14</div>
            <h2>Acceptance of Terms</h2>
          </div>
          <div class="card-content">
            <p>
              By using this website, you are hereby accepting the terms and
              conditions stipulated within the Privacy Policy Agreement. If you
              are not in agreement with our terms and conditions, then you
              should refrain from further use of our sites. In addition, your
              continued use of our website following the posting of any updates
              or changes to our terms and conditions shall mean that you agree
              and acceptance of such changes.
            </p>
          </div>
        </section>

        <!-- How to Contact Us -->
        <section id="contact" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 15</div>
            <h2>How to Contact Us</h2>
          </div>
          <div class="card-content">
            <p>
              If you have any questions or concerns regarding the Privacy Policy
              Agreement related to our website, please feel free to contact us
              at the following email, telephone number or mailing address.
            </p>

            <div class="contact-card-box highlight-contact">
              <h4>Contact Information</h4>
              <div class="contact-details">
                <div class="detail-row">
                  <span class="label">Mailing Address:</span>
                  <span class="value"
                    >SML - Space Maintainer Laboratories<br />
                    9129 Lurline Ave. Chatsworth, California 91311</span
                  >
                </div>
                <div class="detail-row">
                  <span class="label">Telephone:</span>
                  <a href="tel:8004233270" class="value link">(800) 423-3270</a>
                </div>
                <div class="detail-row">
                  <span class="label">Email:</span>
                  <a href="mailto:Info@smlglobal.com" class="value link"
                    >Info@smlglobal.com</a
                  >
                </div>
              </div>
            </div>

            <p>
              The data controller responsible for your personal information for
              the purposes of GDPR compliance is:
            </p>
            <div class="contact-card-box">
              <p class="contact-name">
                <strong>John Doe</strong>
              </p>
              <div class="contact-details">
                <div class="detail-row">
                  <span class="label">Mailing Address:</span>
                  <span class="value"
                    >9129 Lurline Ave. Chatsworth, California 91311</span
                  >
                </div>
                <div class="detail-row">
                  <span class="label">Telephone:</span>
                  <span class="value link">(818) 998-7460</span>
                </div>
                <div class="detail-row">
                  <span class="label">Email:</span>
                  <span class="value link">johndoep@smlglobal.com</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Back to top banner -->
        <div class="back-to-top-card">
          <div>
            <h3>Questions about our Policy?</h3>
            <p>Our legal and customer support teams are here to help you.</p>
          </div>
          <div class="btn-group">
            <button
              class="btn btn-outline"
              @click="scrollToSection('introduction')"
            >
              ↑ Back to Top
            </button>
            <router-link to="/" class="btn btn-primary">
              Return to Store
            </router-link>
          </div>
        </div>
      </main>
    </div>

    <!-- ================= FOOTER ================= -->
    <footer>
      <div class="wrap">
        <div class="footer-grid">
          <div>
            <div class="logo" style="color: #fff">Ora<span>klenz</span></div>
            <p
              style="
                font-size: 13.5px;
                color: #8fa29e;
                max-width: 280px;
                margin-top: 12px;
              "
            >
              Dentist-formulated gum health gel from SML, a 50+ year dental
              supply manufacturer.
            </p>
          </div>
          <div>
            <h4>Shop</h4>
            <router-link to="/#pricing-section"> Single Tube </router-link>
            <router-link to="/#pricing-section"> 3-Tube Bundle </router-link>
            <router-link to="/#pricing-section"> 5-Tube Bundle </router-link>
          </div>
          <div>
            <h4>Learn</h4>
            <router-link to="/#compare"> Compare </router-link>
            <router-link to="/#faq"> FAQ </router-link>
            <router-link to="/#video"> Watch Video </router-link>
          </div>
          <div>
            <h4>Support &amp; Legal</h4>
            <a href="mailto:Victorp@smlglobal.com">Contact Us</a>
            <router-link to="/terms-of-service"> Terms of Service </router-link>
            <router-link to="/privacy-policy"> Privacy Policy </router-link>
          </div>
        </div>
        <div class="disclaimer">
          © Space Maintainers Labs, Inc. (SML Global). All rights reserved. SML
          and Oraklenz are trademarks of Space Maintainers Labs, Inc. 9129
          Lurline Avenue, Chatsworth, California 91311.
        </div>
      </div>
    </footer>

    <!-- ================= CART DRAWER ================= -->
    <q-dialog
      v-model="isCartOpen"
      position="right"
      maximized
      @hide="handleDialogHide"
    >
      <q-card class="column full-height" style="width: 400px; max-width: 100vw">
        <q-card-section class="row items-center q-pb-none q-pt-md q-px-md">
          <div class="text-h5 text-weight-bold flex items-center">
            <q-btn
              v-if="isCheckoutStep"
              icon="arrow_back"
              flat
              round
              dense
              class="q-mr-sm"
              @click="isCheckoutStep = false"
            />
            {{ isCheckoutStep ? "Checkout" : "Your Cart" }}
          </div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>

        <q-card-section class="col q-pt-lg q-px-md scroll">
          <div v-if="!isCheckoutStep">
            <div
              v-if="cartStore.items.length === 0"
              class="text-center text-grey-7 q-pa-md"
            >
              Your cart is empty.
            </div>
            <template v-else>
              <q-list>
                <q-item
                  v-for="l in cartStore.items || []"
                  :key="l.key"
                  class="q-px-none q-mb-md items-start"
                >
                  <q-item-section avatar>
                    <div class="cart-line-thumb">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#0077be"
                        stroke-width="2"
                      >
                        <path
                          d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"
                        />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                      </svg>
                    </div>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-bold">
                      {{ l.name }}
                    </q-item-label>
                    <q-item-label caption class="q-mt-xs">
                      Qty: {{ l.qty }} × ${{ l.price.toFixed(2) }}
                    </q-item-label>
                    <q-item-label>
                      <span
                        class="text-negative text-caption cursor-pointer"
                        @click="removeFromCart(l.key)"
                      >
                        Remove
                      </span>
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side top>
                    <div class="text-weight-bold text-body2">
                      ${{ (l.price * l.qty).toFixed(2) }}
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </div>

          <div v-else>
            <q-form class="q-gutter-md">
              <q-input
                v-model="checkoutForm.name"
                outlined
                label="Full Name"
                dense
              />
              <q-input
                v-model="checkoutForm.email"
                outlined
                label="Email Address"
                type="email"
                dense
              />
              <q-input
                v-model="checkoutForm.address"
                outlined
                label="Shipping Address"
                dense
              />
              <q-input
                v-model="checkoutForm.card"
                outlined
                label="Card Number"
                placeholder="**** **** **** ****"
                dense
              />
              <div class="row q-col-gutter-sm">
                <div class="col-6">
                  <q-input
                    v-model="checkoutForm.exp"
                    outlined
                    label="MM/YY"
                    dense
                  />
                </div>
                <div class="col-6">
                  <q-input
                    v-model="checkoutForm.cvv"
                    outlined
                    label="CVV"
                    dense
                  />
                </div>
              </div>
            </q-form>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-actions class="column q-pa-md">
          <div
            class="row full-width justify-between q-mb-md text-weight-bold text-h6"
          >
            <span>Subtotal</span>
            <span>${{ (cartStore.subtotal || 0).toFixed(2) }}</span>
          </div>
          <q-btn
            v-if="!isCheckoutStep"
            color="dark"
            class="full-width q-py-sm text-weight-bold"
            unelevated
            label="Proceed to Checkout"
            @click="checkout()"
          />
          <q-btn
            v-else
            color="primary"
            class="full-width q-py-sm text-weight-bold"
            unelevated
            label="Complete Order"
            @click="completeOrder()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<style scoped>
/* Scoped & Theme Styles */
.legal-page {
  --ink: #002244;
  --ink-soft: #4a6b8c;
  --paper: #ffffff;
  --paper-raised: #f7fafc;
  --teal-deep: #00437c;
  --teal-bright: #0077be;
  --teal-pale: #e6f0f9;
  --coral: #0077be;
  --coral-dark: #00437c;
  --line: #d1e3f0;
  --line-subtle: #e8f0f6;
  --gold: #c9a24b;
  --warning-bg: #fffbf0;
  --warning-border: #fce8b2;
  --warning-text: #8c5b00;
  --radius: 14px;
  --maxw: 1220px;
  --font-display: "Fraunces", serif;
  --font-body: "Inter", sans-serif;
  --font-mono: "IBM Plex Mono", monospace;

  background: #fbfdfe;
  color: var(--ink);
  font-family: var(--font-body);
  line-height: 1.6;
}

.wrap {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 24px;
}

.brand-link {
  text-decoration: none;
}

/* Announcement Bar */
.announce {
  background: var(--teal-deep);
  color: #eaf6f3;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 12.5px;
  letter-spacing: 0.03em;
  padding: 9px 12px;
}

.announce b {
  color: #fff;
}

/* Header */
header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--line);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  max-width: var(--maxw);
  margin: 0 auto;
}

.navlinks {
  display: flex;
  align-items: center;
  gap: 26px;
  font-size: 14.5px;
  font-weight: 500;
}

.navlinks a {
  text-decoration: none;
  color: var(--ink-soft);
  transition: color 0.15s ease;
}

.navlinks a:hover {
  color: var(--teal-bright);
}

.navlinks .active-nav {
  color: var(--teal-deep);
  font-weight: 600;
  background: var(--teal-pale);
  padding: 4px 12px;
  border-radius: 999px;
}

.navright {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cart-btn {
  position: relative;
  background: none;
  border: 1.5px solid var(--line);
  border-radius: 999px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.cart-btn:hover {
  background-color: var(--teal-pale);
  border-color: var(--teal-bright);
}

.cart-count {
  position: absolute;
  top: -6px;
  right: -6px;
  background: var(--coral);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  width: 19px;
  height: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 14.5px;
  padding: 10px 22px;
  border-radius: 999px;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.btn-primary {
  background: var(--coral);
  color: #fff;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 119, 190, 0.3);
}

.btn-outline {
  background: transparent;
  color: var(--teal-deep);
  border: 1.5px solid var(--teal-deep);
}

.btn-outline:hover {
  background: var(--teal-deep);
  color: #fff;
}

@media (max-width: 860px) {
  .navlinks {
    display: none;
  }
}

/* Legal Hero */
.legal-hero {
  background: linear-gradient(180deg, #f0f7fc 0%, #fbfdfe 100%);
  padding: 38px 0 28px;
  border-bottom: 1px solid var(--line-subtle);
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: var(--ink-soft);
  margin-bottom: 16px;
}

.breadcrumbs a {
  color: var(--ink-soft);
  text-decoration: none;
}

.breadcrumbs a:hover {
  color: var(--teal-bright);
  text-decoration: underline;
}

.breadcrumbs .separator {
  color: #9cb5cb;
}

.breadcrumbs .current {
  color: var(--teal-deep);
  font-weight: 600;
}

.hero-content {
  max-width: 800px;
}

.eyebrow {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--teal-deep);
  font-weight: 600;
  margin-bottom: 8px;
}

.page-title {
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  line-height: 1.15;
  color: var(--ink);
  margin: 0 0 12px;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 17px;
  color: var(--ink-soft);
  margin: 0 0 20px;
  line-height: 1.5;
}

.meta-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid var(--line);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  color: var(--ink-soft);
  box-shadow: 0 2px 5px rgba(0, 34, 68, 0.03);
}

.meta-badge strong {
  color: var(--ink);
}

.meta-badge svg {
  color: var(--teal-bright);
}

/* Notice Callout */
.notice-callout {
  background: #f0f7ff;
  border: 1px solid #c8e1f5;
  border-left: 4px solid #0077be;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.callout-icon {
  color: #0077be;
  flex-shrink: 0;
  margin-top: 2px;
}

.callout-text {
  font-size: 14.5px;
  line-height: 1.55;
  color: #1a4268;
}

/* Layout Grid */
.legal-body-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  padding-top: 20px;
  padding-bottom: 60px;
  align-items: start;
}

@media (max-width: 980px) {
  .legal-body-layout {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .legal-sidebar {
    position: static !important;
    top: auto !important;
  }
}

/* Sidebar TOC */
.legal-sidebar {
  position: sticky;
  top: 90px;
  max-height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
}

.sidebar-inner {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 4px 16px rgba(0, 34, 68, 0.04);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
}

.sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--teal-deep);
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line-subtle);
  margin-bottom: 10px;
}

.toc-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  padding-right: 4px;
  max-height: 380px;
}

.toc-link {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  text-align: left;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 13px;
  color: var(--ink-soft);
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;
}

.toc-link:hover {
  background: var(--teal-pale);
  color: var(--teal-deep);
}

.toc-link.active {
  background: var(--teal-pale);
  color: var(--teal-deep);
  font-weight: 600;
  border-left: 3px solid var(--teal-bright);
}

.toc-num {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #7b9ab8;
  flex-shrink: 0;
}

.toc-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-contact-card {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--line-subtle);
}

.sidebar-contact-card h4 {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 4px;
}

.sidebar-contact-card p {
  font-size: 12px;
  color: var(--ink-soft);
  margin: 0 0 10px;
  line-height: 1.4;
}

.sidebar-contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--teal-pale);
  color: var(--teal-deep);
  font-size: 12px;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: 6px;
  text-decoration: none;
  width: 100%;
  justify-content: center;
  transition: background 0.15s ease;
}

.sidebar-contact-btn:hover {
  background: #d4e7f7;
}

/* Legal Cards */
.legal-content {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.legal-card {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 32px;
  box-shadow: 0 2px 10px rgba(0, 34, 68, 0.03);
  scroll-margin-top: 100px;
}

.legal-card.warning-card {
  border-color: #f1dfb8;
  background: linear-gradient(180deg, #fffdf8 0%, #ffffff 100%);
}

.card-header {
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--line-subtle);
}

.section-tag {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--teal-bright);
  background: var(--teal-pale);
  padding: 3px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.section-tag.tag-warning {
  color: #946200;
  background: #feefc3;
}

.legal-card h2 {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  margin: 0;
  letter-spacing: -0.01em;
}

.legal-card h3 {
  font-family: var(--font-body);
  font-size: 16.5px;
  font-weight: 700;
  color: var(--ink);
  margin: 20px 0 8px;
}

.legal-card p {
  font-size: 15px;
  color: #27415e;
  margin: 0 0 14px;
  line-height: 1.65;
}

.legal-card a {
  color: var(--teal-bright);
  font-weight: 500;
  text-decoration: underline;
}

.legal-card a:hover {
  color: var(--teal-deep);
}

.company-address-box {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--teal-pale);
  border: 1px solid #cce3f5;
  border-radius: 10px;
  padding: 14px 18px;
  margin: 16px 0 20px;
  color: var(--teal-deep);
  font-size: 14.5px;
}

.highlight-quote {
  margin: 14px 0 18px;
  padding: 12px 18px;
  background: #f4f8fb;
  border-left: 4px solid var(--teal-bright);
  border-radius: 0 8px 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  font-style: italic;
}

.styled-list {
  margin: 10px 0 18px 20px;
  padding: 0;
  color: #27415e;
  font-size: 14.5px;
}

.styled-list li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.styled-numbered-list {
  margin: 14px 0 20px 22px;
  padding: 0;
  color: #27415e;
  font-size: 14px;
}

.styled-numbered-list li {
  margin-bottom: 12px;
  line-height: 1.6;
}

.caps-bold-lead {
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  color: var(--ink);
  margin-bottom: 14px;
}

.sub-alert-box {
  background: #fbfcfe;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 18px 20px;
  margin-top: 18px;
}

.sub-alert-box h4 {
  font-size: 15px;
  font-weight: 700;
  color: var(--teal-deep);
  margin: 0 0 8px;
}

.sub-alert-box p {
  font-size: 14px;
  margin-bottom: 10px;
}

.sub-alert-box p:last-child {
  margin-bottom: 0;
}

.contact-card-box {
  background: #f4f8fc;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 22px 24px;
  margin: 20px 0 10px;
}

.contact-card-box.highlight-contact {
  background: #edf5fc;
  border-color: #b9daf2;
}

.contact-card-box h4 {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 6px;
}

.contact-name {
  font-size: 15px;
  color: var(--teal-deep);
  margin-bottom: 14px !important;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  font-size: 14px;
}

@media (max-width: 600px) {
  .detail-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}

.detail-row .label {
  font-weight: 600;
  color: var(--ink-soft);
}

.detail-row .value {
  color: var(--ink);
}

.detail-row .link {
  color: var(--teal-bright);
  font-weight: 600;
}

.back-to-top-card {
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 28px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.back-to-top-card h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 4px;
}

.back-to-top-card p {
  font-size: 14px;
  color: var(--ink-soft);
  margin: 0;
}

.back-to-top-card .btn-group {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

@media (max-width: 680px) {
  .back-to-top-card {
    flex-direction: column;
    text-align: center;
  }
  .back-to-top-card .btn-group {
    width: 100%;
    justify-content: center;
  }
}

/* Footer */
footer {
  background: var(--ink);
  color: #d1e3f0;
  padding: 52px 0 28px;
  margin-top: 40px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 32px;
}

@media (max-width: 760px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
  }
}

footer h4 {
  color: #fff;
  font-size: 13px;
  font-family: var(--font-mono);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin: 0 0 14px;
}

footer a {
  display: block;
  color: #d1e3f0;
  text-decoration: none;
  font-size: 13.5px;
  margin-bottom: 9px;
  transition: color 0.15s ease;
}

footer a:hover {
  color: #fff;
}

.disclaimer {
  font-size: 11.5px;
  color: #7292b3;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 36px;
  padding-top: 22px;
  line-height: 1.6;
}

/* Cart Thumb */
.cart-line-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: var(--teal-pale);
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
